<template>
  <div class="te">
    <!-- Header -->
    <div class="te-header">
      <!-- Row 1: title -->
      <div class="te-title-row">
        <router-link :to="{ name: 'email-templates-list' }" class="te-back">
          <i class="material-icons">arrow_back</i>
        </router-link>
        <h2 class="te-title">{{ isEdit ? 'Edit Template' : 'New Template' }}</h2>
      </div>
      <!-- Row 2: fields + actions -->
      <div class="te-fields-row">
        <div class="te-fields">
          <div class="te-field">
            <label class="te-label">Name</label>
            <input v-model="form.name" class="te-input" placeholder="Untitled template" />
          </div>
          <div class="te-field">
            <label class="te-label">Subject</label>
            <input v-model="form.subject" class="te-input" placeholder="Email subject line" />
          </div>
          <div class="te-field te-field--narrow">
            <label class="te-label">Category</label>
            <select v-model="form.category" class="te-input te-select">
              <option v-for="opt in categoryOptions" :key="opt.value" :value="opt.value">{{ opt.text }}</option>
            </select>
          </div>
        </div>
        <div class="te-actions">
          <button class="te-btn te-btn--ghost" @click="openPreview" :disabled="isSaving">
            <i class="material-icons" style="font-size:16px;">visibility</i> Preview
          </button>
          <button class="te-btn te-btn--primary" @click="saveTemplate" :disabled="isSaving">
            <i class="material-icons" style="font-size:16px;">save</i>
            {{ isSaving ? 'Saving...' : 'Save Template' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Editor area -->
    <div class="te-body">
      <template v-if="editorReady">
        <!-- No layout chosen yet → show layout picker -->
        <LayoutPicker
          v-if="!editorData.layout"
          @select-layout="onSelectLayout"
          @select-template="onSelectTemplate"
        />
        <!-- Layout chosen → show content editor -->
        <ContentEditor
          v-else
          :layout="editorData.layout"
          :content="editorData.content"
          :on-upload="handleAssetUpload"
          @update:content="onContentUpdate"
          @change-layout="onChangeLayout"
        />
      </template>
    </div>

    <!-- Preview modal -->
    <TemplatePreviewModal v-model="showPreview" :html="previewHtml" />
  </div>
</template>

<script>
import { mapActions } from 'vuex';
import { EMAIL_TEMPLATE_CATEGORIES } from '../../common/constants';
import { renderLayout, getDefaultContent } from '../../helpers/email-layouts';
import LayoutPicker from '../../components/EmailEditor/LayoutPicker.vue';
import ContentEditor from '../../components/EmailEditor/ContentEditor.vue';
import TemplatePreviewModal from './TemplatePreviewModal.vue';

export default {
  name: 'TemplateEditor',
  components: { LayoutPicker, ContentEditor, TemplatePreviewModal },

  data() {
    return {
      form: {
        name: '',
        subject: '',
        body_html: '',
        body_text: '',
        category: 'general',
        variables: [],
      },
      editorData: {
        layout: null,
        content: {},
      },
      isEdit: false,
      isSaving: false,
      editorReady: false,
      showPreview: false,
      previewHtml: '',
      categoryOptions: EMAIL_TEMPLATE_CATEGORIES,
    };
  },

  methods: {
    ...mapActions('emailTemplate', [
      'getEmailTemplate',
      'createEmailTemplate',
      'updateEmailTemplate',
      'getAssetUploadUrl',
      'previewTemplate',
    ]),

    buildBodyHtml() {
      if (!this.editorData.layout) return '';
      return renderLayout(this.editorData.layout, this.editorData.content);
    },

    onSelectLayout(layoutId) {
      this.editorData = {
        layout: layoutId,
        content: getDefaultContent(layoutId),
      };
    },

    onSelectTemplate(tpl) {
      // Deep clone the template content so edits don't mutate the original
      var content = JSON.parse(JSON.stringify(tpl.content));
      this.editorData = {
        layout: tpl.layout,
        content: content,
      };
      // Pre-fill the template name if empty
      if (!this.form.name) {
        this.form.name = tpl.name;
      }
    },

    onContentUpdate(content) {
      this.editorData = Object.assign({}, this.editorData, { content: content });
    },

    onChangeLayout() {
      this.editorData = { layout: null, content: {} };
    },

    async loadTemplate() {
      var id = this.$route.params.id;
      if (!id) {
        this.editorReady = true;
        return;
      }
      this.isEdit = true;
      try {
        var res = await this.getEmailTemplate(id);
        var data = res.data;
        this.form = {
          name: data.name || '',
          subject: data.subject || '',
          body_html: data.body_html || '',
          body_text: data.body_text || '',
          category: data.category || 'general',
          variables: data.variables || [],
        };
        // Restore editor_data if it has our new layout format
        var ed = data.editor_data;
        if (ed && ed.layout) {
          this.editorData = { layout: ed.layout, content: ed.content || {} };
        }
        // Legacy GrapesJS data or no editor_data → show LayoutPicker (user re-authors)
      } catch (err) {
        this.makeToast('danger', 'Failed to load template');
      }
      this.editorReady = true;
    },

    async saveTemplate() {
      if (!this.form.name || !this.form.subject) {
        this.makeToast('danger', 'Name and subject are required');
        return;
      }
      if (!this.editorData.layout) {
        this.makeToast('danger', 'Please select a layout first');
        return;
      }
      this.isSaving = true;
      try {
        var bodyHtml = this.buildBodyHtml();
        var user = this.$store.getters['auth/getLoggedInUser'];
        var payload = {
          school: user.school.id,
          name: this.form.name,
          subject: this.form.subject,
          body_html: bodyHtml,
          body_text: this.form.body_text,
          category: this.form.category,
          variables: this.form.variables,
          editor_data: {
            layout: this.editorData.layout,
            content: this.editorData.content,
          },
        };
        if (this.isEdit) {
          await this.updateEmailTemplate({ id: this.$route.params.id, data: payload });
          this.makeToast('success', 'Template updated');
        } else {
          var res = await this.createEmailTemplate(payload);
          this.makeToast('success', 'Template created');
          var newId = res.data.data ? res.data.data.id : res.data.id;
          this.$router.replace({ name: 'email-template-edit', params: { id: newId } });
          this.isEdit = true;
        }
      } catch (err) {
        this.makeToast('danger', 'Failed to save template');
      } finally {
        this.isSaving = false;
      }
    },

    async openPreview() {
      if (!this.isEdit) {
        this.makeToast('danger', 'Save the template before previewing');
        return;
      }
      try {
        var bodyHtml = this.buildBodyHtml();
        var res = await this.previewTemplate({ id: this.$route.params.id, body_html: bodyHtml });
        this.previewHtml = res.data.data ? res.data.data.html : res.data.html;
        this.showPreview = true;
      } catch (err) {
        this.makeToast('danger', 'Failed to generate preview');
      }
    },

    async handleAssetUpload(file) {
      try {
        var res = await this.getAssetUploadUrl({ file_name: file.name, content_type: file.type });
        var uploadUrl = res.data.data ? res.data.data.upload_url : res.data.upload_url;
        if (!uploadUrl) throw new Error('No upload URL returned');
        var putRes = await fetch(uploadUrl, { method: 'PUT', headers: { 'Content-Type': file.type }, body: file });
        if (!putRes.ok) throw new Error('S3 upload returned ' + putRes.status);
        return uploadUrl.split('?')[0];
      } catch (err) {
        console.error('Image upload error:', err);
        this.makeToast('danger', 'Image upload failed');
        return null;
      }
    },
  },

  created() {
    this.loadTemplate();
  },
};
</script>

<style scoped>
.te {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 64px);
  background: #f4f3ed;
}

.te-header {
  background: #fff;
  border-bottom: 1px solid #e2e6ea;
  flex-shrink: 0;
  padding: 14px 24px 16px 4px;
}

.te-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 14px;
}
.te-back {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  color: #5f6368;
  text-decoration: none;
  transition: background 0.15s;
  flex-shrink: 0;
}
.te-back:hover { background: #f4f3ed; color: #181818; }
.te-title {
  font-weight: 600;
  color: #181818;
  margin: 0;
}

.te-fields-row {
  display: flex;
  align-items: flex-end;
  gap: 16px;
}
.te-fields {
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex: 1;
  min-width: 0;
}
.te-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
}

.te-field { display: flex; flex-direction: column; gap: 4px; flex: 1; min-width: 120px; }
.te-field--narrow { flex: 0 0 150px; min-width: 150px; }
.te-label { font-size: 11px; font-weight: 600; color: #5f6368; text-transform: uppercase; letter-spacing: 0.4px; }
.te-input {
  height: 36px;
  padding: 0 10px;
  border: 1px solid #dadce0;
  border-radius: 6px;
  font-size: 13px;
  color: #181818;
  background: #fff;
  outline: none;
  transition: border-color 0.15s;
}
.te-input:focus { border-color: var(--primary-color, #3b86f5); }
.te-select { cursor: pointer; }

.te-btn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
  padding: 0 16px;
  height: 36px;
  border: none;
  border-radius: 6px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  white-space: nowrap;
  transition: background 0.15s, box-shadow 0.15s;
}
.te-btn:disabled { opacity: 0.55; cursor: not-allowed; }
.te-btn--ghost {
  background: transparent;
  color: #5f6368;
  border: 1px solid #dadce0;
}
.te-btn--ghost:hover:not(:disabled) { background: #f4f3ed; }
.te-btn--primary {
  background: var(--primary-color, #3b86f5);
  color: #fff;
}
.te-btn--primary:hover:not(:disabled) { background: #2b6ed4; box-shadow: 0 1px 3px rgba(0,0,0,0.2); }

.te-body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}
</style>
